import { Stack } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCodingScreen } from '../../interface';
import type { CodeReviewAction, CodeReviewCommand, TransactionCode, TransactionSectionsData } from '../../interface/transactionCodes';
import { SECTION_GAP } from '../../styles/tokens';
import { AddCodeButton } from './AddCodeButton';
import { CodeSection } from './CodeSection';
import { ProvidersSection } from './ProvidersSection';

export const TransactionSections: React.FC<{
  readonly transaction: TransactionSectionsData;
  readonly reorderable?: boolean;
}> = ({ transaction, reorderable = true }) => {
  const { api, bus } = useCodingScreen();
  const container = useRef<HTMLDivElement>(null);
  const [expandedId, setExpandedId] = useState<string | undefined>();
  const [hint, setHint] = useState<{ codeId: string; action: CodeReviewAction } | undefined>();
  const toggle = (id: string) => {
    setHint(undefined);
    setExpandedId((current) => (current === id ? undefined : id));
  };

  useEffect(() => {
    if (!hint) return;
    const timer = setTimeout(() => setHint(undefined), 4000);
    return () => clearTimeout(timer);
  }, [hint]);

  const trigger = useCallback((code: TransactionCode, action: CodeReviewAction, source: CodeReviewCommand['source']) => {
    const command: CodeReviewCommand = {
      action,
      transactionId: transaction.id,
      codeId: code.id,
      codeType: code.codeType,
      code: code.code,
      source
    };
    const event = `code.${action}` as const;
    const handled = bus?.emit(event, command) ?? false;
    setHint(handled ? undefined : { codeId: code.id, action });
  }, [bus, transaction.id]);

  useEffect(() => {
    if (!bus) return;
    const codes = [...(transaction.procedure ? [transaction.procedure] : []), ...transaction.modifiers, ...transaction.diagnoses];
    const code = codes.find((item) => item.id === expandedId);
    const unsubscribes = (['accept', 'replace', 'decline'] as const).map((action) => {
      const event = `codeReview.${action}` as const;
      return bus.on(event, () => {
        if (!code) return;
        const row = Array.from<Element>(container.current?.querySelectorAll('[data-code-id]') ?? [])
          .find((element) => element.getAttribute('data-code-id') === expandedId);
        if (row?.contains(document.activeElement)) trigger(code, action, 'keyboard');
      });
    });
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [bus, expandedId, transaction.procedure, transaction.modifiers, transaction.diagnoses, trigger]);

  const onCodeAction = (code: TransactionCode, action: CodeReviewAction) => trigger(code, action, 'button');
  const reorder = api?.reorderTransactionCodes;
  const onReorderFor = (codeType: 'MODIFIER' | 'ICD') =>
    reorder && reorderable
      ? (orderedIds: readonly string[]) =>
          reorder({ transactionId: transaction.id, codeType, orderedCodes: orderedIds })
      : undefined;

  return (
    <Stack ref={container} spacing={SECTION_GAP}>
      <CodeSection
        label="Procedures"
        sectionKey="procedures"
        codes={transaction.procedure ? [transaction.procedure] : []}
        expandedId={expandedId}
        onToggleCode={toggle}
        onCodeAction={onCodeAction}
        hint={hint}
        addButton={transaction.procedure ? undefined : <AddCodeButton codeType="CPT" transactionId={transaction.id} />}
      />
      <CodeSection
        label="Modifiers"
        sectionKey="modifiers"
        codes={transaction.modifiers}
        expandedId={expandedId}
        onToggleCode={toggle}
        onCodeAction={onCodeAction}
        hint={hint}
        addButton={<AddCodeButton codeType="MODIFIER" transactionId={transaction.id} />}
        onReorder={onReorderFor('MODIFIER')}
      />
      <CodeSection
        label="Diagnoses"
        sectionKey="diagnoses"
        codes={transaction.diagnoses}
        expandedId={expandedId}
        onToggleCode={toggle}
        onCodeAction={onCodeAction}
        hint={hint}
        addButton={<AddCodeButton codeType="ICD" transactionId={transaction.id} />}
        onReorder={onReorderFor('ICD')}
      />
      <ProvidersSection providers={transaction.providers} />
    </Stack>
  );
};
